import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { addIndication, INDICATION_TYPES } from 'modules/taboola-common-frontend-modules/Indications';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { addProtocol } from 'modules/taboola-common-frontend-modules/utils/urlUtils';
import { usePixelTesterApi } from 'services/api/pixelTesterApi';
import { errorMessagesUtils } from 'services/utils';

export const generateErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: (
        <FormattedMessage
            id="pixel.tester.website.test-events.error"
            defaultMessage="Something went wrong, please try again later or contact support"
        />
    ),
});

export const generateBadResponseErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: (
        <FormattedMessage
            id="pixel.tester.website.test-events.bad-response.error"
            defaultMessage="Unable to get test url, please try again later or contact support"
        />
    ),
});

export const useGetPixelTesterUrl = websiteUrl => {
    const { accountName } = useAccount();
    const dispatch = useDispatch();
    const testLinkApi = usePixelTesterApi();

    const { onChange: onPixelTesterUrlChange } = useFormFieldValue({
        field: 'pixelTesterUrl',
    });

    const getTestLink = useCallback(
        async queryParams => {
            try {
                return testLinkApi.getPixelTester(accountName, queryParams);
            } catch (e) {
                dispatch(addIndication(generateErrorIndication(e)));
                throw e;
            }
        },
        [testLinkApi, accountName, dispatch]
    );

    return useCallback(async () => {
        const testLink = await getTestLink({ url: websiteUrl });
        if (!testLink?.url) {
            dispatch(addIndication(generateBadResponseErrorIndication()));
            return;
        }

        const canonicalUrl = addProtocol(testLink.url);
        const urlObj = new URL(canonicalUrl);
        onPixelTesterUrlChange(urlObj.toString());
    }, [dispatch, getTestLink, onPixelTesterUrlChange, websiteUrl]);
};
